import { Button } from 'components/Button/Button';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
  getVerificationStatus,
  handleVerificationError,
} from 'utils/apiResponseErrorHandler';
import { ConfettiBombardment } from './ConfettiBombardment';
import { VERIFICATION_STATUS, VERIFICATION_TEXT } from './utils/constant';
import './Verification.scss';

// SIGNED_TOKEN_REGEX matches signed tokens: 48 hex chars + "." + 64 hex chars (HMAC-SHA256)
// See: internal/gigacore/repository/email_verification.go
const SIGNED_TOKEN_REGEX = /^[0-9a-fA-F]{48}\.[0-9a-fA-F]{64}$/;

/**
 * Reusable Verification Page Component
 * Optimized with React.memo and useCallback for performance
 * Can be used for email verification, password reset, and other verification flows
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.type - Type of verification ('email', 'password-reset', 'generic')
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Page subtitle
 * @param {string} props.successTitle - Title shown on success
 * @param {string} props.successMessage - Message shown on success
 * @param {string} props.expiredTitle - Title shown when link expires
 * @param {string} props.expiredMessage - Message shown when link expires
 * @param {string} props.errorTitle - Title shown on error
 * @param {string} props.errorMessage - Message shown on error
 * @param {Function} props.onVerify - Function to call for verification (REQUIRED)
 * @param {Object} props.successActions - Actions to show on success
 * @param {Object} props.errorActions - Actions to show on error
 * @param {boolean} props.isRetryVerification - Whether this is a retry verification
 *
 * @returns {JSX.Element} Verification component
 *
 * @example
 * // Basic usage
 * <Verification
 *   type="email"
 *   title="Email Verification"
 *   onVerify={handleEmailVerification}
 * />
 *
 * @example
 * // With custom actions
 * <Verification
 *   type="password-reset"
 *   title="Reset Password"
 *   successActions={[{ label: "Continue", onClick: handleContinue }]}
 *   onVerify={handlePasswordReset}
 * />
 */
const Verification = ({
  type = 'generic',
  title = 'Verification',
  subtitle = 'Please verify your request to continue',
  successTitle = 'Verification Successful!',
  successMessage = 'Your verification has been completed successfully.',
  successIcons,
  expiredTitle = 'Verification Link Expired',
  expiredMessage = 'Your verification link has expired. Please request a new one.',
  errorTitle = 'Verification Failed',
  errorMessage = 'Verification failed. Please try again.',
  onVerify,
  successActions,
  errorActions,
  isRetryVerification = false,
  infoText,
  staticMode = false,
  staticStatus = VERIFICATION_STATUS.SUCCESS,
  staticMessage = '',
  showSuccessTitle = true,
}) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(VERIFICATION_STATUS.LOADING);
  const [message, setMessage] = useState('');
  const [triggerConfetti, setTriggerConfetti] = useState(false);

  const token = searchParams.get('token');
  const hasPerformedVerification = useRef(false);

  // Memoized verification result handler
  const handleVerificationResult = useCallback(
    (result) => {
      try {
        const nextStatus = getVerificationStatus(result);
        setStatus(nextStatus);

        if (nextStatus === VERIFICATION_STATUS.SUCCESS) {
          setMessage(successMessage);
          setTriggerConfetti(true); // 🎉 Trigger celebration!
        } else if (nextStatus === VERIFICATION_STATUS.EXPIRED) {
          setMessage(expiredMessage);
        } else {
          setMessage(result.error || errorMessage);
        }
      } catch (error) {
        const { errorMessage: errorMsg, status: errorStatus } =
          handleVerificationError(error);
        setStatus(errorStatus);
        setMessage(errorMsg);
      }
    },
    [errorMessage, expiredMessage, successMessage]
  );

  // Memoized verification function
  const performVerification = useCallback(
    async (isRetry = false) => {
      // Prevent multiple calls unless it's a retry
      if (!isRetry && hasPerformedVerification.current) {
        return;
      }

      try {
        // Token validation - only signed tokens are supported
        const normalizedToken = typeof token === 'string' ? token.trim() : '';
        if (!SIGNED_TOKEN_REGEX.test(normalizedToken)) {
          setStatus(VERIFICATION_STATUS.ERROR);
          setMessage(VERIFICATION_TEXT.invalidToken);
          return;
        }

        // Function validation
        if (!onVerify) {
          setStatus(VERIFICATION_STATUS.ERROR);
          setMessage(VERIFICATION_TEXT.verificationFnMissing);
          return;
        }

        // Mark as performed only for initial verification
        if (!isRetry) {
          hasPerformedVerification.current = true;
        }

        setStatus(VERIFICATION_STATUS.LOADING);
        const result = await onVerify(normalizedToken);
        handleVerificationResult(result);
      } catch (error) {
        const { errorMessage: errorMsg, status: errorStatus } =
          handleVerificationError(error);
        setStatus(errorStatus);
        setMessage(errorMsg);
      }
    },
    [handleVerificationResult, onVerify, token]
  );

  // Memoized action renderer
  const renderAction = useCallback(() => {
    return (
      <div className='verification-action-buttons'>
        {isRetryVerification && (
          <Button onClick={() => performVerification(true)} variant='outline'>
            Retry Verification
          </Button>
        )}
        {errorActions &&
          errorActions.length > 0 &&
          errorActions.map((action, index) => (
            <Button
              key={action.label || index}
              onClick={action.onClick}
              className={action.className}
              disabled={action.disabled}
              variant={action.variant || 'primary-gradient'}
            >
              {action.label}
            </Button>
          ))}
      </div>
    );
  }, [isRetryVerification, errorActions, performVerification]);

  const renderButtons = useCallback((actions, defaultVariant) => {
    if (!actions || actions.length === 0) {
      return null;
    }

    return (
      <div className='action-buttons'>
        {actions.map((action, index) => (
          <Button
            key={action.label || index}
            onClick={action.onClick}
            className={action.className}
            disabled={action.disabled}
            variant={action.variant || defaultVariant}
          >
            {action.label}
          </Button>
        ))}
      </div>
    );
  }, []);

  const renderStatusLayout = useCallback(
    ({ wrapperClass, icon, titleNode, messageNode, infoNode, actionsNode }) => {
      return (
        <div className={wrapperClass}>
          {icon}
          {titleNode}
          {messageNode}
          {infoNode}
          {actionsNode}
        </div>
      );
    },
    []
  );

  // Memoized content renderer
  const renderContent = useCallback(() => {
    switch (status) {
      case VERIFICATION_STATUS.LOADING:
        return (
          <div className='verification-loading'>
            <div className='loading-spinner'></div>
            <h5>{VERIFICATION_TEXT.loadingTitle}</h5>
            <p>{VERIFICATION_TEXT.loadingMessage}</p>
          </div>
        );

      case VERIFICATION_STATUS.SUCCESS:
        return renderStatusLayout({
          wrapperClass: 'verification-success',
          icon: successIcons ? (
            <div className='success-icons'>{successIcons}</div>
          ) : (
            <div className='success-icon'>✓</div>
          ),
          titleNode: showSuccessTitle ? <h5>{successTitle}</h5> : null,
          messageNode: message ? <p>{message}</p> : null,
          infoNode: infoText ? (
            <div className='verification-info-text'>{infoText}</div>
          ) : null,
          actionsNode: renderButtons(successActions, ''),
        });

      case VERIFICATION_STATUS.EXPIRED:
        return renderStatusLayout({
          wrapperClass: 'verification-expired',
          icon: <div className='expired-icon'>⏰</div>,
          titleNode: <h5>{expiredTitle}</h5>,
          messageNode: <p>{message}</p>,
          actionsNode: renderAction(),
        });

      case VERIFICATION_STATUS.ERROR:
      default:
        return renderStatusLayout({
          wrapperClass: 'verification-error',
          icon: <div className='error-icon'>☹️</div>,
          titleNode: <h5>{errorTitle}</h5>,
          messageNode: <p>{message}</p>,
          actionsNode:
            isRetryVerification || errorActions ? renderAction() : null,
        });
    }
  }, [
    status,
    successTitle,
    message,
    successActions,
    successIcons,
    showSuccessTitle,
    infoText,
    expiredTitle,
    errorTitle,
    renderAction,
    renderButtons,
    renderStatusLayout,
    isRetryVerification,
    errorActions,
  ]);

  // Initial verification effect
  useEffect(() => {
    if (staticMode) {
      setStatus(staticStatus);
      setMessage(staticMessage);
      return;
    }

    if (!token) {
      setStatus(VERIFICATION_STATUS.ERROR);
      setMessage(VERIFICATION_TEXT.noTokenProvided);
      return;
    }

    // Only run on mount - performVerification is intentionally excluded
    // because we use hasPerformedVerification ref to prevent double calls
    if (!hasPerformedVerification.current) {
      performVerification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, staticMode]); // Intentionally exclude performVerification

  return (
    <div className='verification-page' data-verification-type={type}>
      <div className='verification-card'>
        <div className='verification-header'>
          <h4>{title}</h4>
          <p className='page-description'>{subtitle}</p>
        </div>
        <div className='verification-content'>{renderContent()}</div>
      </div>
      {/* 🎉 Confetti celebration for successful verification */}
      {triggerConfetti && (
        <ConfettiBombardment
          trigger={triggerConfetti}
          duration={5000}
          onCompletion={() => setTriggerConfetti(false)}
        />
      )}
    </div>
  );
};

// Set display name for better debugging
Verification.displayName = 'Verification';

Verification.propTypes = {
  type: PropTypes.oneOf(['email', 'password-reset', 'generic']),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  successTitle: PropTypes.string,
  successMessage: PropTypes.string,
  successIcons: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  expiredTitle: PropTypes.string,
  expiredMessage: PropTypes.string,
  errorTitle: PropTypes.string,
  errorMessage: PropTypes.string,
  onVerify: PropTypes.func,
  successActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      className: PropTypes.string,
      disabled: PropTypes.bool,
      variant: PropTypes.string,
    })
  ),
  errorActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      className: PropTypes.string,
      disabled: PropTypes.bool,
      variant: PropTypes.string,
    })
  ),
  isRetryVerification: PropTypes.bool,
  infoText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  staticMode: PropTypes.bool,
  staticStatus: PropTypes.oneOf(Object.values(VERIFICATION_STATUS)),
  staticMessage: PropTypes.string,
};

export default Verification;
export { Verification };
